<template>
  <div>
    <vxe-card>
      <template #title>
        <vxe-button content="tab.pickingModeConfig" mode="text" status="primary"/>
      </template>
      <template #extra>
        <vxe-button content="button.refresh" icon="vxe-icon-refresh" mode="text" status="danger" @click="refresh(warehouse)"/>
        <vxe-button content="button.add" icon="vxe-icon-add" mode="text" status="primary" @click="add"/>
      </template>
      <vxe-table ref="table" :data="list" :loading="loading">
        <vxe-column fixed="left" type="seq" width="40"/>
        <vxe-column :cell-render="{name:'FormatSelect',options:pickingModes}" field="pickingMode" title="business.label.pickingMode" width="200"/>
        <vxe-column :cell-render="{name:'FormatSelect',options:visualizedData}" field="visualized" title="business.label.visualized" width="200"/>
        <vxe-column field="orderProperties" title="business.label.orderProperty" width="200">
          <template #default="{ row }">{{ getStrings(row.orderProperties, 'name') }}</template>
        </vxe-column>
        <vxe-column field="storageRooms" title="business.label.storageRoom">
          <template #default="{ row }">{{ getStrings(row.storageRooms, 'name') }}</template>
        </vxe-column>
        <vxe-column fixed="right" title="business.label.operate" width="auto">
          <vxe-button-group slot-scope="{row}" mode="text">
            <vxe-button content="button.edit" icon="vxe-icon-edit" status="primary" @click.stop="edit(row,false)"/>
            <vxe-button content="button.copy" icon="vxe-icon-copy" status="primary" @click.stop="edit(row,true)"/>
            <vxe-button content="button.delete" icon="vxe-icon-delete" status="danger" @click.stop="remove(row)"/>
          </vxe-button-group>
        </vxe-column>
      </vxe-table>
    </vxe-card>
    <vxe-modal v-model="visible" :loading="loading" :title="$t('tab.pickingModeConfig')" type="confirm" width="40%" @confirm="confirm">
      <vxe-form ref="form" :data="config" :rules="rules" span="12">
        <vxe-form-item :item-render="{name:'VxeSelect',options:pickingModes}" field="pickingMode" title="business.label.pickingMode"/>
        <vxe-form-item :item-render="{name:'VxeSelect',options:visualizedData}" field="visualized" title="business.label.visualized"/>
        <vxe-form-item span="24" title="business.label.orderProperty">
          <template #default>
            <vxe-select v-model="orderPropertyIds" :options="orderProperties" multiple @change="onOrderPropertyChange"/>
          </template>
        </vxe-form-item>
        <vxe-form-item field="storageRooms" span="24" title="business.label.storageRoom">
          <template #default>
            <vxe-input :editable="false" :value="getStrings(config.storageRooms,'name')" @clear="onStorageRoomChange([])" @click="onStorageRoomSelect"/>
          </template>
        </vxe-form-item>
      </vxe-form>
    </vxe-modal>
    <storage-room-modal ref="storageRoomModal" select-type="checkbox" @change="onStorageRoomChange"/>
  </div>
</template>

<script>
import { deleteOne, findAll, findOne, save } from '@/api/Api'
import { Warehouse } from '@/api/dto/common/Warehouse'
import { PickingModeConfig } from '@/api/dto/config/PickingModeConfig'
import { Dictionary } from '@/api/dto/admin/Dictionary'
import { StorageRoom } from '@/api/dto/common/StorageRoom'
import { getStrings } from '@/utils/util'
import StorageRoomModal from '@/views/modal/StorageRoomModal.vue'

export default {
  components: { StorageRoomModal },
  props: {
    pickingModes: { type: Array, default: [] },
    orderProperties: { type: Array, default: [] }
  },
  data() {
    return {
      loading: false,
      visible: false,
      warehouse: new Warehouse(),
      list: [],
      config: new PickingModeConfig(),
      visualizedData: [
        { value: true, label: '是' },
        { value: false, label: '否' },
        { value: null, label: '无' }
      ],
      rules: {
        pickingMode: [{ required: true, message: this.$t('message.cannotBeEmpty') }]
      }
    }
  },
  computed: {
    orderPropertyIds() {
      return this.config.orderProperties ? this.config.orderProperties.map(item => item.id) : []
    }
  },
  methods: {
    getStrings,
    onOrderPropertyChange(event) {
      this.config.orderProperties = event.value ? event.value.map(item => {
        return new Dictionary({ id: item })
      }) : []
    },
    onStorageRoomSelect() {
      this.$refs.storageRoomModal.open({ 'warehouse.id': this.warehouse.id, available: true, physical: true }, this.config, this.config.storageRooms)
    },
    onStorageRoomChange(values) {
      this.config.storageRooms = values.map(item => {
        return new StorageRoom(item)
      })
    },
    refresh(warehouse) {
      this.warehouse = warehouse
      if (this.warehouse.isNew()) {
        return this.$message.warning('请先选择仓库')
      }
      this.loading = true
      findAll('pickingModeConfigFactory', { 'warehouse.id': this.warehouse.id }).then(res => {
        if (res.success) {
          this.list = res.result.map(item => {
            return new PickingModeConfig(item)
          })
        }
      }).finally(() => this.loading = false)
    },
    add() {
      this.visible = true
      this.config = new PickingModeConfig()
    },
    edit(row, copy) {
      this.visible = true
      this.loading = true
      findOne('pickingModeConfigFactory', { id: row.id }).then(res => {
        if (res.success) {
          if (copy) {
            res.result.id = undefined
            res.result.version = undefined
          }
          this.config = new PickingModeConfig(res.result)
        }
      }).finally(() => this.loading = false)
    },
    save() {
      this.loading = true
      const config = this.config.toJSONObject()
      config.warehouse = this.warehouse
      save('pickingModeConfigPersister', config).then(res => {
        if (res.success) {
          this.config = new PickingModeConfig(res.result)
          this.$message.success(this.$t('message.saveSuccess'))
          this.refresh(this.warehouse)
          this.visible = false
        }
      }).finally(() => this.loading = false)
    },
    async confirm() {
      if (!await this.$refs.form.validate()) this.save()
    },
    remove(row) {
      if (row.id) {
        this.loading = true
        deleteOne('pickingModeConfigDeleter', { id: row.id }).then(res => {
          if (res.success) {
            this.refresh(this.warehouse)
            this.$message.success(this.$t('message.deleteSuccess'))
          }
        }).finally(() => this.loading = false)
      } else {
        this.list.splice(this.list.indexOf(row), 1)
      }
    }
  }
}
</script>